/**
 * Chat-text sanitisation.
 *
 * The plan-chat model is asked to return a JSON envelope shaped like
 * `{ "text": "...", "actions": [...] }`. parseChatJson then pulls out
 * `text` and stores it as the assistant's chat message. Most of the
 * time this works.
 *
 * Failure modes we've seen in the wild:
 *   - Model puts a literal JSON dump INSIDE the `text` string —
 *     e.g. text="Here's the plan: { ... }" or text="{ \"sessions\":
 *     [...] }". The envelope parses fine, but rendering `text`
 *     verbatim shows raw JSON in the chat bubble.
 *   - Model writes a code-fenced block inside text.
 *   - Model returns text=null and only actions, but our fallback
 *     "Done." is too curt for the user to know what changed.
 *
 * `sanitiseChatText` is the safety net. It returns:
 *   - the cleaned text if it looks like prose,
 *   - a friendly stand-in if it looks like raw JSON / a code block,
 *   - a sensible fallback if it's empty.
 *
 * Used in two places (defence in depth):
 *   - server-side in worker/ai-plan.ts before persisting.
 *   - client-side in web/phases/PlanPhase.tsx before rendering, so
 *     historical rows that escaped the server guard still display
 *     cleanly.
 */

export const CHAT_FALLBACK_TEXT =
  "I made the changes you asked for. (My note was a bit garbled — let me know if anything looks off.)";

export const CHAT_FALLBACK_EMPTY = 'Done.';

export function sanitiseChatText(raw: string | null | undefined): string {
  if (raw == null) return CHAT_FALLBACK_EMPTY;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return CHAT_FALLBACK_EMPTY;

  // Looks-like-JSON heuristics. Each on its own is a strong signal.
  // We err on the side of suppressing — if a real user sentence
  // accidentally contains "{...}" the cost is they see the
  // friendly fallback instead, which is much better than dumping a
  // 2KB JSON blob into the chat bubble.
  if (looksLikeJson(trimmed)) return CHAT_FALLBACK_TEXT;
  if (looksLikeFence(trimmed)) return CHAT_FALLBACK_TEXT;

  // Strip any inline code blocks that aren't dominating the text but
  // would still surprise a user reading their plan-chat history.
  // (Plain backticks in prose are fine; multi-line fenced blocks
  // are the giveaway.)
  return stripFencedBlocks(trimmed);
}

function looksLikeJson(s: string): boolean {
  // Strong start-of-string signal.
  if (/^[\s\n]*[{[]/.test(s)) return true;
  // Field markers that don't appear in conversational English.
  // Each pattern matches the bare JSON-key form (possibly with a
  // trailing colon). Two or more hits = JSON, not English.
  const markers = [
    /"text"/,
    /"actions"/,
    /"sessions"/,
    /"create_session"/,
    /"update_session"/,
    /"delete_session"/,
    /"startMinute"/,
    /"endMinute"/,
    /"themeIds"/,
    /"ownerId"/,
    /"colorHue"/,
  ];
  // Two or more markers ≈ JSON. One alone could be a coincidence.
  let hits = 0;
  for (const m of markers) {
    if (m.test(s)) hits += 1;
    if (hits >= 2) return true;
  }
  return false;
}

function looksLikeFence(s: string): boolean {
  // Triple-backtick block that takes up most of the message.
  const m = s.match(/```[\s\S]*?```/);
  if (!m) return false;
  return m[0].length / s.length > 0.5;
}

function stripFencedBlocks(s: string): string {
  // Remove ```…``` blocks but keep surrounding prose.
  const without = s.replace(/```[\s\S]*?```/g, '').trim();
  // If stripping leaves nothing useful, use the friendly stand-in.
  if (without.length < 4) return CHAT_FALLBACK_TEXT;
  return without;
}
